import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';
import Button from './ui/Button';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';
import { scrollToSection } from '../lib/utils';

const Navbar = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: t('nav.solutions'), id: 'solutions' },
    { label: t('nav.industries'), id: 'industries' },
    { label: t('nav.process'), id: 'process' },
    { label: t('nav.caseStudies'), id: 'case-studies' },
    { label: t('nav.about'), id: 'about' },
    { label: t('nav.contact'), id: 'contact' },
  ];

  const handleNavClick = (id) => {
    scrollToSection(id);
    setMobileMenuOpen(false);
  };

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? theme === 'dark'
            ? 'bg-afsc-slate/90 backdrop-blur-md shadow-lg'
            : 'bg-afsc-offwhite/90 backdrop-blur-md shadow-lg'
          : theme === 'dark'
          ? 'md:bg-transparent bg-afsc-slate/90 backdrop-blur-md'
          : 'md:bg-transparent bg-afsc-offwhite/90 backdrop-blur-md'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div
            onClick={() => scrollToSection('hero')}
            className="cursor-pointer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Logo 
              size="xl" 
            />
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4 rtl:gap-reverse">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNavClick(link.id)}
                className={`text-sm ${
                  theme === 'dark' ? 'text-white/90' : 'text-afsc-charcoal/90'
                } hover:text-afsc-gold transition-colors duration-200 font-medium whitespace-nowrap`}
              >
                {link.label}
              </button>
            ))}
            <div className="flex items-center gap-2 ml-2 rtl:mr-2 rtl:ml-0 pl-2 rtl:pr-2 rtl:pl-0 border-l rtl:border-r rtl:border-l-0 border-white/10 rtl:border-white/10">
              <LanguageToggle />
            <ThemeToggle />
            </div>
            <Button
              variant="primary"
              onClick={() => handleNavClick('contact')}
              className="ml-1 rtl:mr-1 rtl:ml-0 text-sm px-4 py-2"
            >
              {t('nav.letsTalk')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden ${theme === 'dark' ? 'text-white' : 'text-afsc-charcoal'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {mobileMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className="md:hidden py-4 border-t border-white/10 overflow-hidden"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleNavClick(link.id)}
                  className={`block w-full text-left py-2 ${
                    theme === 'dark' ? 'text-white/90' : 'text-afsc-charcoal/90'
                  } hover:text-afsc-gold transition-colors`}
                >
                  {link.label}
                </button>
              ))}
              <div className="mt-4 flex items-center gap-4">
                <LanguageToggle />
                <ThemeToggle />
                <Button
                  variant="primary"
                  onClick={() => handleNavClick('contact')}
                  className="flex-1"
                >
                  {t('nav.letsTalk')}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;

